import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  CheckCircle2,
  ShieldCheck,
  Bike,
  Wrench,
  Ruler,
  Truck,
  Award
} from 'lucide-react';

/**
 * Cycling Equipment Service Cards
 * Based on wireframe specifications with fit small tile sizes
 */

// Card A: Primary Anchor - Bike Assembly
export const BikeAssemblyCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-cyan-200 bg-cyan-50/20'
      }`}
      data-testid="bike-assembly-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Cycling Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🚴</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Bike Assembly</h3>
            <p className="text-xs text-gray-500">Boxed bikes · Safety checked</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-cyan-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Brake &amp; gear adjustment</span>
          </p>
          <p className="text-xs text-cyan-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Road-ready setup</span>
          </p>
          <p className="text-xs text-cyan-700 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 flex-shrink-0" />
            <span>Insured mechanic</span>
          </p>
        </div>

        {/* Rating & Location */}
        <div className="flex items-center justify-between text-xs mb-2">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
            <span className="text-gray-500">({provider?.review_count || 212} builds)</span>
          </div>
          {provider?.travel_radius_km && (
            <span className="text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {provider.travel_radius_km} km
            </span>
          )}
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 40}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-cyan-600 hover:bg-cyan-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-bike-assembly"
        >
          Book assembly
        </Button>
      </CardContent>
    </Card>
  );
};

// Card B: Companion - Safety Check & Tune-Up
export const SafetyTuneUpCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  trigger = 'inline', // 'inline', 'post_diy', 'post_delivery'
}) => {
  const triggerMessages = {
    inline: null,
    post_diy: "Skipped assembly? Get a safety check!",
    post_delivery: "Bike delivered! Make sure it's road-ready",
  };

  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-amber-200 bg-amber-50/20'
      }`}
      data-testid="safety-tuneup-card"
    >
      <CardContent className="p-3">
        {/* Trigger Message */}
        {triggerMessages[trigger] && (
          <p className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1 mb-2">
            {triggerMessages[trigger]}
          </p>
        )}

        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Safety</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🛠️</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Bike Safety Check &amp; Tune-Up</h3>
            <p className="text-xs text-gray-500">Brakes · Gears · Bolts</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Ideal after DIY assembly</span>
          </p>
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Same-day available</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
          <span className="text-gray-500">({provider?.review_count || 320} checks)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 25}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-amber-600 hover:bg-amber-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-safety-tuneup"
        >
          Book tune-up
        </Button>
      </CardContent>
    </Card>
  );
};

// Card C: Performance - Bike Fit
export const BikeFitCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-purple-200 bg-purple-50/20'
      }`}
      data-testid="bike-fit-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Performance</span>
          </div>
        )}

        {/* Premium Badge */}
        <Badge className="bg-purple-100 text-purple-700 text-xs mb-2">
          <Award className="h-3 w-3 mr-1" />
          Premium Service
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">📏</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Bike Fit &amp; Comfort Setup</h3>
            <p className="text-xs text-gray-500">Posture · Saddle · Reach</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Injury prevention</span>
          </p>
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <Award className="h-3 w-3 flex-shrink-0" />
            <span>Certified fitter</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
          <span className="text-gray-500">({provider?.review_count || 98} fits)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 80}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-purple-600 hover:bg-purple-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-bike-fit"
        >
          Book bike fit
        </Button>
      </CardContent>
    </Card>
  );
};

// Card D: Convenience - Mobile Bike Repair
export const MobileBikeRepairCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-emerald-200 bg-emerald-50/20'
      }`}
      data-testid="mobile-bike-repair-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Mobile Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🚐</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Mobile Bike Repair</h3>
            <p className="text-xs text-gray-500">Home · Office · On-site</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>No transport needed</span>
          </p>
          <p className="text-xs text-emerald-700 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 flex-shrink-0" />
            <span>Insured</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.7'}</span>
          <span className="text-gray-500">({provider?.review_count || 140} visits)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 35}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-emerald-600 hover:bg-emerald-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-mobile-repair"
        >
          Book mobile repair
        </Button>
      </CardContent>
    </Card>
  );
};

// Cycling Bundle Card
export const CyclingBundleCard = ({
  bundle,
  providers,
  currency = '£',
  onBook,
}) => {
  // Bundle type specific styles
  const getBundleStyle = () => {
    switch (bundle.id) {
      case 'new_bike_essentials':
        return { border: 'border-cyan-300', bg: 'from-cyan-50', accent: 'bg-cyan-600' };
      case 'comfort_performance':
        return { border: 'border-purple-300', bg: 'from-purple-50', accent: 'bg-purple-600' };
      case 'mobile_convenience':
        return { border: 'border-emerald-300', bg: 'from-emerald-50', accent: 'bg-emerald-600' };
      case 'kids_bike_bundle':
        return { border: 'border-pink-300', bg: 'from-pink-50', accent: 'bg-pink-600' };
      default:
        return { border: 'border-blue-300', bg: 'from-blue-50', accent: 'bg-blue-600' };
    }
  };

  const style = getBundleStyle();

  return (
    <Card 
      className={`overflow-hidden border-2 ${style.border} bg-gradient-to-br ${style.bg} to-white hover:shadow-lg transition-all`}
      data-testid={`cycling-bundle-card-${bundle.id}`}
    >
      <CardContent className="p-4">
        {/* Popular Badge */}
        {bundle.is_popular && (
          <Badge className="bg-yellow-500 text-white text-xs mb-2">
            ⭐ Most Popular
          </Badge>
        )}

        {/* Discount Badge */}
        <Badge className={`${style.accent} text-white text-xs mb-2`}>
          Save {currency}{bundle.savings || Math.round(bundle.original_price * bundle.discount_percent / 100)}
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-3">
          <span className="text-2xl">{bundle.icon || '🚴'}</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">{bundle.name}</h3>
            <p className="text-xs text-gray-500">{bundle.description}</p>
          </div>
        </div>

        {/* Included Services */}
        <div className="space-y-1 mb-3">
          {bundle.included_services?.map((serviceType, idx) => (
            <p key={idx} className="text-xs text-gray-700 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-green-600" />
              <span>{serviceType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            </p>
          ))}
        </div>

        {/* Pricing */}
        <div className="flex items-center gap-2 mb-3">
          <span className="font-bold text-lg">
            From {currency}{bundle.final_price}
          </span>
          {bundle.original_price > bundle.final_price && (
            <span className="text-gray-400 line-through text-sm">
              {currency}{bundle.original_price}
            </span>
          )}
        </div>

        {/* CTA */}
        <Button 
          className={`w-full ${style.accent} hover:opacity-90`}
          onClick={() => onBook?.(bundle)}
          data-testid={`book-cycling-bundle-${bundle.id}`}
        >
          Book bundle
        </Button>
      </CardContent>
    </Card>
  );
};

// Post-Checkout Upsell Card for Cycling
export const CyclingPostCheckoutCard = ({
  productType,
  prompt,
  service,
  provider,
  onAdd,
  onDismiss,
}) => {
  return (
    <Card 
      className="border-2 border-cyan-300 bg-gradient-to-r from-cyan-50 to-white"
      data-testid="cycling-post-checkout-upsell"
    >
      <CardContent className="p-3 flex items-center gap-3">
        <span className="text-2xl">🚴</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{prompt?.title || 'Your bike is on the way!'}</p>
          <p className="text-xs text-gray-500">{prompt?.subtitle || 'Want it built safely?'}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="text-xs h-8"
            onClick={onDismiss}
          >
            Not now
          </Button>
          <Button 
            size="sm" 
            className="text-xs h-8 bg-cyan-600 hover:bg-cyan-700"
            onClick={() => onAdd?.(service, provider)}
          >
            {prompt?.cta || 'Add service'} · {provider?.currency || '£'}{service?.price || 40}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Mobile Sticky Pill for Cycling
export const CyclingMobilePill = ({
  startingPrice = 25,
  currency = '£',
  onTap,
}) => {
  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 lg:hidden"
      data-testid="cycling-mobile-pill"
    >
      <button
        onClick={onTap}
        className="flex items-center gap-2 px-4 py-3 bg-cyan-600 text-white rounded-full shadow-lg hover:bg-cyan-700 transition-all active:scale-95"
      >
        <span>🚴</span>
        <span className="text-sm font-medium">Bike help nearby</span>
        <span className="text-sm">From {currency}{startingPrice}</span>
      </button>
    </div>
  );
};

// Mobile Bottom Sheet Content
export const CyclingMobileSheet = ({
  services,
  providers,
  onBook,
  currency = '£',
}) => {
  const serviceTypes = [
    { type: 'bike_assembly', icon: '🚴', name: 'Bike Assembly', defaultPrice: 40, label: 'builds' },
    { type: 'safety_tuneup', icon: '🛠️', name: 'Safety Tune-Up', defaultPrice: 25, label: 'checks' },
    { type: 'bike_fit', icon: '📏', name: 'Bike Fit', defaultPrice: 80, label: 'fits' },
    { type: 'mobile_repair', icon: '🚐', name: 'Mobile Repair', defaultPrice: 35, label: 'visits' },
  ];

  return (
    <div className="p-4">
      <h3 className="font-bold text-lg mb-4">Cycling Services Near You</h3>
      <div className="space-y-3">
        {serviceTypes.map((st) => {
          const service = services?.find(s => s.type === st.type);
          const provider = providers?.find(p => p.services?.some(s => s.type === st.type));
          const price = service?.price || st.defaultPrice;
          const rating = provider?.rating || 4.8;
          const reviews = provider?.review_count || 100;

          return (
            <div 
              key={st.type}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{st.icon}</span>
                <div>
                  <p className="font-medium text-sm">{st.name}</p>
                  <p className="text-xs text-gray-500">From {currency}{price}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-xs">
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                  <span>{rating.toFixed(1)}</span>
                </div>
                <Button 
                  size="sm" 
                  className="h-8 bg-cyan-600 hover:bg-cyan-700"
                  onClick={() => onBook?.(service || { type: st.type, name: st.name, price }, provider)}
                >
                  Book
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default {
  BikeAssemblyCard,
  SafetyTuneUpCard,
  BikeFitCard,
  MobileBikeRepairCard,
  CyclingBundleCard,
  CyclingPostCheckoutCard,
  CyclingMobilePill,
  CyclingMobileSheet,
};
